import { useCallback } from 'react';
import { useAuthStore } from '@store/auth.store';
import { useBillingCounterStore } from '@store/billing-counter.store';
import { login as loginRequest, verifyMfa as verifyMfaRequest } from '@services/auth.service';
import { isMfaChallenge } from '@app-types/auth.types';
import type { LoginRequest, AuthUser, LoginResponse, LoginResult, MfaVerifyRequest } from '@app-types/auth.types';

export interface UseAuthResult {
  user: AuthUser | null;
  isAuthenticated: boolean;
  roles: string[];
  permissions: string[];
  tenantId: string | null;
  /** Returns a full LoginResult on success, or an MfaChallengeResponse if MFA must be completed via verifyMfa() first. Does NOT establish a session by itself when a challenge is returned. */
  login: (request: LoginRequest) => Promise<LoginResponse>;
  /**
   * Completes a login that returned an MFA challenge. Deliberately does
   * NOT establish the session itself — GuestRoute redirects away from
   * /login the instant isAuthenticated flips true, which would unmount
   * LoginPage (and the one-time RecoveryCodesDialog with it) before the
   * codes were ever shown, found live via production verification. The
   * caller shows recovery codes first (if any), then calls
   * completeSession() explicitly once the user has acknowledged them.
   */
  verifyMfa: (request: MfaVerifyRequest) => Promise<LoginResult>;
  completeSession: (result: LoginResult) => void;
  logout: () => void;
}

export function useAuth(): UseAuthResult {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const roles = useAuthStore((state) => state.roles);
  const permissions = useAuthStore((state) => state.permissions);
  const tenantId = useAuthStore((state) => state.tenantId);
  const setSession = useAuthStore((state) => state.setSession);
  const clearSession = useAuthStore((state) => state.clearSession);

  const login = useCallback(
    async (request: LoginRequest) => {
      const result = await loginRequest(request);
      if (!isMfaChallenge(result)) {
        setSession(result.tokens, result.user);
      }
      return result;
    },
    [setSession],
  );

  const verifyMfa = useCallback(async (request: MfaVerifyRequest) => verifyMfaRequest(request), []);

  const completeSession = useCallback(
    (result: LoginResult) => {
      setSession(result.tokens, result.user);
    },
    [setSession],
  );

  const logout = useCallback(() => {
    // See services/auth.service.ts's own header — no server call exists to make here.
    clearSession();
    /**
     * THE COUNTER BELONGS TO THE OPERATOR, NOT THE BROWSER.
     *
     * It is persisted to `localStorage` and was surviving sign-out, so the next
     * person to sign in at that machine silently inherited the last one's desk
     * — and every bill they took was attributed to it. A shift change IS "same
     * machine, different cashier", which is exactly the case the sign-in gate
     * exists to catch, and keeping the value was what stopped it from asking.
     *
     * Cleared here rather than in the store's own `persist` config because this
     * is the only place that knows a SESSION ended; a page reload must still
     * keep the answer (`CounterSelectionGate`'s header: re-asking on every load
     * is re-asking a question whose answer is "the same desk as ten seconds
     * ago").
     */
    useBillingCounterStore.getState().setCounter(null);
  }, [clearSession]);

  return { user, isAuthenticated, roles, permissions, tenantId, login, verifyMfa, completeSession, logout };
}
